import { useState } from "react";
import { api } from "../api.js";
import { useApp } from "../app.jsx";
import { navigate } from "../router.jsx";
import { money, fmtDateShort, pct, daysUntil, today, addDays } from "../format.js";
import {
  Async, Badge, Bar, Button, Card, Check, Chips, Empty, ErrorPanel, Field, Fields, Grid, Input, KPI,
  Modal, Select, Table, TopBar, Wrap, useAsync, useToast,
} from "../ui.jsx";

/** Hourly projects burn hours; flat-fee projects burn the fee. */
const budgetPct = (p) =>
  p.billing_type === "hourly" ? pct(p.hours, p.budget_hours) : pct(p.billed, p.budget_amount);

const commitment = (p) =>
  p.billing_type === "hourly"
    ? Number(p.budget_hours || 0) * Number(p.hourly_rate || 0)
    : Number(p.budget_amount || 0);

export default function Projects() {
  const { settings, reloadApp, me } = useApp();
  const state = useAsync(() => api.get("projects"), []);
  const [filter, setFilter] = useState("active");
  const [creating, setCreating] = useState(false);
  const sym = settings.currency_symbol;

  return (
    <>
      <TopBar title="Projects">
        {me.permissions["projects.manage"] && (
          <Button kind="pri" onClick={() => setCreating(true)}>
            ＋ New project
          </Button>
        )}
      </TopBar>

      <Wrap>
        <Async state={state}>
          {(all) => {
            const active = all.filter((p) => p.status === "active");
            const complete = all.filter((p) => p.status === "complete");
            const rows = filter === "all" ? all : all.filter((p) => p.status === filter);

            const clientCount = new Set(active.map((p) => p.client_name)).size;
            const trackedHours = all.reduce((s, p) => s + Number(p.hours || 0), 0);
            const committed = active.reduce((s, p) => s + commitment(p), 0);
            const hot = active
              .map((p) => ({ p, used: budgetPct(p) }))
              .filter((x) => x.used > 85)
              .sort((a, b) => b.used - a.used);

            return (
              <>
                <Grid cols={4}>
                  <KPI
                    label="Active projects"
                    value={active.length}
                    sub={`across ${clientCount} client${clientCount === 1 ? "" : "s"}`}
                  />
                  <KPI
                    label="Hours tracked"
                    value={trackedHours.toFixed(1)}
                    sub={`across ${all.length} project${all.length === 1 ? "" : "s"}`}
                  />
                  <KPI label="Committed value" value={money(committed, sym)} sub="open engagements" />
                  <KPI
                    label="Over budget"
                    value={hot.length}
                    tone={hot.length ? "warn" : undefined}
                    sub={hot.length ? `${hot[0].p.name} at ${hot[0].used}%` : "every project within budget"}
                  />
                </Grid>

                <Card
                  className="mt"
                  bare
                  title={
                    <Chips
                      value={filter}
                      onChange={setFilter}
                      options={[
                        { key: "active", label: "Active", count: active.length },
                        { key: "complete", label: "Complete", count: complete.length },
                        { key: "all", label: "All", count: all.length },
                      ]}
                    />
                  }
                >
                  <Table
                    rows={rows}
                    onRowClick={(p) => navigate(`/projects/${p.id}`)}
                    empty={<Empty icon="❏">No {filter === "all" ? "" : filter} projects yet</Empty>}
                    columns={[
                      {
                        key: "name",
                        label: "Project",
                        render: (p) => (
                          <>
                            <b>{p.name}</b>
                            <div className="tiny muted">
                              {Number(p.member_count || 0)} {Number(p.member_count) === 1 ? "person" : "people"}
                            </div>
                          </>
                        ),
                      },
                      { key: "client_name", label: "Client", render: (p) => <span className="sm">{p.client_name}</span> },
                      {
                        key: "billing",
                        label: "Billing",
                        render: (p) => (
                          <span className="sm dim">
                            {p.billing_type === "hourly" ? `${money(p.hourly_rate, sym)}/hr` : "Flat fee"}
                          </span>
                        ),
                      },
                      {
                        key: "hours",
                        label: "Hours",
                        align: "right",
                        render: (p) => (
                          <span className="num">
                            {Number(p.hours || 0).toFixed(1)}
                            {p.billing_type === "hourly" && Number(p.budget_hours || 0) ? (
                              <span className="muted"> / {Number(p.budget_hours).toFixed(0)}</span>
                            ) : null}
                          </span>
                        ),
                      },
                      {
                        key: "budget",
                        label: "Budget",
                        width: 170,
                        render: (p) => {
                          const used = budgetPct(p);
                          return (
                            <div className="row">
                              <div style={{ flex: 1 }}>
                                <Bar pct={used} warn={used > 85} />
                              </div>
                              <span className="tiny num" style={{ width: 34, textAlign: "right" }}>
                                {used}%
                              </span>
                            </div>
                          );
                        },
                      },
                      {
                        key: "due_on",
                        label: "Due",
                        render: (p) => {
                          const soon = p.status === "active" && p.due_on && daysUntil(p.due_on) < 14;
                          return (
                            <span
                              className={`sm ${soon ? "" : "dim"}`}
                              style={soon ? { color: "var(--warn)", fontWeight: 600 } : undefined}
                            >
                              {fmtDateShort(p.due_on)}
                            </span>
                          );
                        },
                      },
                      {
                        key: "billed",
                        label: "Billed",
                        align: "right",
                        render: (p) => <span className="num">{money(p.billed, sym)}</span>,
                      },
                      { key: "status", label: "", align: "right", render: (p) => <Badge status={p.status} /> },
                    ]}
                  />
                </Card>
              </>
            );
          }}
        </Async>
      </Wrap>

      {creating && (
        <NewProjectModal
          onClose={() => setCreating(false)}
          onSaved={() => {
            state.reload();
            reloadApp(); // projects feed the time tracker and invoice editor
          }}
        />
      )}
    </>
  );
}

/* -------------------------------------------------------------- new project */

function NewProjectModal({ onClose, onSaved }) {
  const { clients, users } = useApp();
  const toast = useToast();
  const activeClients = clients.filter((c) => c.status === "active");
  const team = users.filter((u) => u.status !== "disabled");

  const [form, setForm] = useState({
    client_id: activeClients[0]?.id || "",
    name: "",
    billing_type: "hourly",
    hourly_rate: "",
    budget_hours: "",
    budget_amount: "",
    starts_on: today(),
    due_on: addDays(today(), 60),
  });
  const [memberIds, setMemberIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const toggle = (id) =>
    setMemberIds((xs) => (xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]));

  async function save() {
    setError(null);
    setBusy(true);
    try {
      const project = await api.post("projects", {
        client_id: form.client_id,
        name: form.name,
        billing_type: form.billing_type,
        hourly_rate: Number(form.hourly_rate) || 0,
        budget_hours: Number(form.budget_hours) || 0,
        budget_amount: Number(form.budget_amount) || 0,
        starts_on: form.starts_on || null,
        due_on: form.due_on || null,
        member_ids: memberIds,
      });
      toast.push(`${project.name} created`);
      onSaved();
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="New project"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button kind="pri" busy={busy} onClick={save}>
            Create project
          </Button>
        </>
      }
    >
      <Fields>
        {error && <ErrorPanel error={error} />}
        <Fields cols={2}>
          <Field label="Client">
            <Select value={form.client_id} onChange={(e) => set("client_id", e.target.value)}>
              <option value="">Pick a client…</option>
              {activeClients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Project name">
            <Input value={form.name} placeholder="Website redesign" onChange={(e) => set("name", e.target.value)} />
          </Field>
        </Fields>

        <Fields cols={3}>
          <Field label="Billing">
            <Select value={form.billing_type} onChange={(e) => set("billing_type", e.target.value)}>
              <option value="hourly">Hourly</option>
              <option value="flat">Flat fee</option>
            </Select>
          </Field>
          {form.billing_type === "hourly" ? (
            <>
              <Field label="Hourly rate">
                <Input value={form.hourly_rate} placeholder="150" onChange={(e) => set("hourly_rate", e.target.value)} />
              </Field>
              <Field label="Budget hours">
                <Input value={form.budget_hours} placeholder="120" onChange={(e) => set("budget_hours", e.target.value)} />
              </Field>
            </>
          ) : (
            <Field label="Flat fee">
              <Input value={form.budget_amount} placeholder="18000" onChange={(e) => set("budget_amount", e.target.value)} />
            </Field>
          )}
        </Fields>

        <Fields cols={2}>
          <Field label="Starts">
            <Input type="date" value={form.starts_on} onChange={(e) => set("starts_on", e.target.value)} />
          </Field>
          <Field label="Due">
            <Input type="date" value={form.due_on} onChange={(e) => set("due_on", e.target.value)} />
          </Field>
        </Fields>

        <Field label="Team on this project" hint="Only these people see it in their timer by default">
          <div className="fields f2" style={{ gap: 8 }}>
            {team.map((u) => (
              <Check key={u.id} label={u.name} checked={memberIds.includes(u.id)} onChange={() => toggle(u.id)} />
            ))}
          </div>
        </Field>
      </Fields>
    </Modal>
  );
}
